import {
  Accordion,
  Anchor,
  Button,
  Divider,
  Group,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import type { Customer } from '@/frontend/hooks/useCustomers'
import { type CreateBoatInput, CreateBoatSchema } from '@/lib/marina/validators'
import { DualUnitInput } from './DualUnitInput'

const BOAT_TYPES = [
  { value: 'POWERBOAT', label: 'Powerboat' },
  { value: 'SAILING_BOAT', label: 'Sailing Boat' },
  { value: 'YACHT', label: 'Yacht' },
  { value: 'CATAMARAN', label: 'Catamaran' },
  { value: 'FISHING', label: 'Fishing' },
  { value: 'DINGHY', label: 'Dinghy' },
  { value: 'OTHER', label: 'Other' },
]

interface Props {
  initialValues?: Partial<CreateBoatInput>
  customers: Customer[]
  onSubmit: (values: CreateBoatInput) => Promise<void> | void
  onCancel: () => void
  submitting?: boolean
  submitLabel?: string
  onAddCustomer?: () => void
}

export function BoatForm({
  initialValues,
  customers,
  onSubmit,
  onCancel,
  submitting,
  submitLabel = 'Create Boat',
  onAddCustomer,
}: Props) {
  const form = useForm<CreateBoatInput>({
    initialValues: {
      customerId: '',
      name: '',
      type: 'OTHER',
      loaMeters: 0,
      beamMeters: 0,
      vesselType: 'PRIVATE',
      color: '',
      manufacturer: '',
      model: '',
      yearBuilt: null,
      draftMeters: null,
      weightTons: null,
      fuelCapacityL: null,
      waterCapacityL: null,
      registration: '',
      imoNumber: '',
      hullId: '',
      flagState: '',
      portOfRegistry: '',
      photo: '',
      location: 'Unknown',
      notes: '',
      ...initialValues,
    } as CreateBoatInput,
    validate: zodResolver(CreateBoatSchema),
  })

  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: `${c.firstName} ${c.lastName} ${c.email ? `(${c.email})` : ''}`.trim(),
  }))

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="lg">
        {/* Sticky top: Customer selection */}
        <Paper p="lg" radius="md">
          <Group justify="space-between" align="flex-end">
            <div style={{ flex: 1 }}>
              <Group gap={4} mb={4}>
                <Text size="sm" fw={600}>
                  Select Customer
                </Text>
                <Text c="red" size="sm">
                  *
                </Text>
              </Group>
              <Text c="dimmed" size="xs" mb={4}>
                If the customer is not listed,{' '}
                {onAddCustomer ? (
                  <Anchor component="button" type="button" onClick={onAddCustomer} c="laut">
                    Add a new customer →
                  </Anchor>
                ) : (
                  <Anchor component="a" href="/dashboard/accounts/customers/new" c="laut">
                    Add a new customer →
                  </Anchor>
                )}
              </Text>
              <Select
                data={customerOptions}
                placeholder="Select a customer"
                searchable
                clearable
                value={form.values.customerId}
                onChange={(v) => form.setFieldValue('customerId', v ?? '')}
                error={form.errors.customerId}
              />
            </div>
          </Group>
        </Paper>

        {/* Boat Details (required) */}
        <Paper p="lg" radius="md">
          <Stack gap="md">
            <div>
              <Title order={4}>Boat Details</Title>
              <Text c="dimmed" size="sm">
                Required information to create a boat record.
              </Text>
            </div>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="Name" placeholder="E.g. Serenity" required {...form.getInputProps('name')} />
              <Select label="Type" required data={BOAT_TYPES} {...form.getInputProps('type')} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <DualUnitInput
                label="LOA"
                required
                description="Length overall"
                placeholder="E.g. 40"
                valueMeters={form.values.loaMeters}
                onChangeMeters={(v) => form.setFieldValue('loaMeters', v ?? 0)}
                error={form.errors.loaMeters as string | undefined}
              />
              <DualUnitInput
                label="Beam"
                required
                description="Width"
                placeholder="E.g. 12"
                valueMeters={form.values.beamMeters}
                onChangeMeters={(v) => form.setFieldValue('beamMeters', v ?? 0)}
                error={form.errors.beamMeters as string | undefined}
              />
            </SimpleGrid>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Vessel Type
              </Text>
              <Text c="dimmed" size="xs" mb="xs">
                Is this vessel for commercial use?
              </Text>
              <Radio.Group
                value={form.values.vesselType}
                onChange={(v) => form.setFieldValue('vesselType', v as CreateBoatInput['vesselType'])}
              >
                <Group gap="md">
                  <Radio value="PRIVATE" label="Private" />
                  <Radio value="COMMERCIAL" label="Commercial" />
                </Group>
              </Radio.Group>
            </div>
          </Stack>
        </Paper>

        {/* Collapsible: Vessel Identity */}
        <Accordion multiple variant="separated" radius="md">
          <Accordion.Item value="identity">
            <Accordion.Control>
              <Stack gap={0}>
                <Text fw={600}>Vessel Identity</Text>
                <Text c="dimmed" size="xs">
                  Descriptive details about the vessel and its origin.
                </Text>
              </Stack>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput label="Color" {...form.getInputProps('color')} />
                <TextInput label="Manufacturer" {...form.getInputProps('manufacturer')} />
                <TextInput label="Model" {...form.getInputProps('model')} />
                <TextInput
                  label="Year Built"
                  type="number"
                  placeholder="E.g. 2020"
                  value={form.values.yearBuilt ?? ''}
                  onChange={(e) =>
                    form.setFieldValue('yearBuilt', e.currentTarget.value ? Number(e.currentTarget.value) : null)
                  }
                  error={form.errors.yearBuilt as string | undefined}
                />
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="measurements">
            <Accordion.Control>
              <Stack gap={0}>
                <Text fw={600}>Measurements</Text>
                <Text c="dimmed" size="xs">
                  Additional vessel measurements.
                </Text>
              </Stack>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <DualUnitInput
                  label="Draft"
                  valueMeters={form.values.draftMeters as number | null}
                  onChangeMeters={(v) => form.setFieldValue('draftMeters', v ?? null)}
                />
                <TextInput
                  label="Weight (tons)"
                  type="number"
                  value={form.values.weightTons ?? ''}
                  onChange={(e) =>
                    form.setFieldValue('weightTons', e.currentTarget.value ? Number(e.currentTarget.value) : null)
                  }
                />
                <TextInput
                  label="Fuel Capacity (L)"
                  type="number"
                  value={form.values.fuelCapacityL ?? ''}
                  onChange={(e) =>
                    form.setFieldValue('fuelCapacityL', e.currentTarget.value ? Number(e.currentTarget.value) : null)
                  }
                />
                <TextInput
                  label="Water Capacity (L)"
                  type="number"
                  value={form.values.waterCapacityL ?? ''}
                  onChange={(e) =>
                    form.setFieldValue('waterCapacityL', e.currentTarget.value ? Number(e.currentTarget.value) : null)
                  }
                />
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="registration">
            <Accordion.Control>
              <Stack gap={0}>
                <Text fw={600}>Registration Details</Text>
                <Text c="dimmed" size="xs">
                  Official registration and identification numbers.
                </Text>
              </Stack>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput label="Registration" {...form.getInputProps('registration')} />
                <TextInput label="IMO Number" {...form.getInputProps('imoNumber')} />
                <TextInput label="Hull Identification Number (HIN)" {...form.getInputProps('hullId')} />
                <TextInput label="Flag State" placeholder="E.g. Indonesia" {...form.getInputProps('flagState')} />
                <TextInput label="Port of Registry" {...form.getInputProps('portOfRegistry')} />
              </SimpleGrid>
              <Textarea label="Notes" rows={3} mt="md" {...form.getInputProps('notes')} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Divider />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" color="laut" loading={submitting}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
